import { useController, type Control, type UseFormRegister, type FieldErrors } from 'react-hook-form';
import { Label } from '@components/ui/Label';
import type { EnumsResponse } from '@/types';
import type { FormData } from './schema';
import { INPUT_CLS, RADIO_CLS } from './constants';
import { Button } from '@/components/ui';
import { Separator } from '@/components/ui/Separator';

interface Step2Props {
  control: Control<FormData>;
  register: UseFormRegister<FormData>;
  errors: FieldErrors<FormData>;
  enums: EnumsResponse | undefined;
  deliveryTimeValue: number;
  objectives: string[];
  constraints: string[];
  onAddObjective: () => void;
  onUpdateObjective: (i: number, v: string) => void;
  onRemoveObjective: (i: number) => void;
  onMoveObjective: (i: number, dir: 'up' | 'down') => void;
  onAddConstraint: () => void;
  onUpdateConstraint: (i: number, v: string) => void;
  onRemoveConstraint: (i: number) => void;
  onBack: () => void;
  onNext: () => void;
}

export function Step2({
  control,
  register,
  errors,
  enums,
  deliveryTimeValue,
  objectives,
  constraints,
  onAddObjective,
  onUpdateObjective,
  onRemoveObjective,
  onMoveObjective,
  onAddConstraint,
  onUpdateConstraint,
  onRemoveConstraint,
  onBack,
  onNext,
}: Step2Props) {
  const { field: budgetField } = useController({ name: 'budget', control });
  const { field: deliveryField } = useController({ name: 'deliveryTime', control });

  return (
    <>
      <h1 className="mb-8 text-3xl font-bold text-[--text-dark-primary]">
        What do you want to achieve?
      </h1>

      <div className="formContainer createProjectForm">
        <div className="flex flex-col gap-[--spacing-14]">
          {/* Key Objectives */}
          <div className="space-y-[--spacing-8]">
            <div className="flex flex-col gap-[--spacing-3]">
              <h2 className="text-xl font-semibold text-[--text-dark-primary]">Key Objectives</h2>
              <p className="text-base">
                Write these in terms of user value or outcomes. For example: &quot;Build a simple
                front-end to interact with my contract&quot;
              </p>
            </div>

            {/* Objective rows */}
            <div className="space-y-[--spacing-8]">
              {objectives.map((obj, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={obj}
                    onChange={(e) => onUpdateObjective(i, e.target.value)}
                    placeholder="Describe an objective..."
                    className={`${INPUT_CLS} flex-1`}
                  />
                  <div className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-md border border-[--base-border] transition-colors hover:text-[--text-dark-primary]">
                    <button
                      type="button"
                      onClick={() => onMoveObjective(i, 'up')}
                      disabled={i === 0}
                      className="flex h-1/2 w-full items-end justify-center transition-colors hover:text-[#f5f5f5] disabled:opacity-30"
                      title="Move Up"
                    >
                      <i className="ri-arrow-up-s-line leading-none" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onMoveObjective(i, 'down')}
                      disabled={i === objectives.length - 1}
                      className="flex h-1/2 w-full items-start justify-center transition-colors hover:text-[#f5f5f5] disabled:opacity-30"
                      title="Move Down"
                    >
                      <i className="ri-arrow-down-s-line leading-none" />
                    </button>
                  </div>
                  <Button
                    type="button"
                    onClick={() => onRemoveObjective(i)}
                    disabled={objectives.length <= 1}
                    variant="secondary"
                    className="text-[20px] h-10 w-10"
                    title="Remove"
                  >
                    <i className="ri-delete-bin-7-line" />
                  </Button>
                </div>
              ))}
            </div>

            <Button type="button" onClick={onAddObjective} variant="link">
              Add Objective
            </Button>
          </div>

          <Separator />

          {/* Technical constraints */}
          <div className="space-y-[--spacing-8]">
            <h2 className="text-xl font-semibold text-[--text-dark-primary]">
              Technical or functional constraints (optional)
            </h2>

            {constraints.length > 0 && (
              <div className="mb-3 space-y-3">
                {constraints.map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={c}
                      onChange={(e) => onUpdateConstraint(i, e.target.value)}
                      placeholder="Describe a constraint..."
                      className={`${INPUT_CLS} flex-1`}
                    />
                    <Button
                      type="button"
                      onClick={() => onRemoveConstraint(i)}
                      variant="secondary"
                      className="text-[20px] h-10 w-10"
                      title="Remove"
                    >
                      <i className="ri-delete-bin-7-line" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <Button
              type="button"
              onClick={onAddConstraint}
              variant="link"
              // className="text-sm font-medium text-[var(--state-brand-active,#36d399)] hover:underline"
            >
              Add Technical/Functional Constraint
            </Button>
          </div>

          <Separator />

          {/* Budget + Delivery time (two columns) */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 text-base [&>div>label]:text-base text-[--text-dark-primary]">
            {/* Budget */}
            <div>
              <Label className="mb-3 block">Indicative budget (USD)</Label>
              <div className="space-y-3">
                {enums?.budgets.map((budget, index) => (
                  <label key={budget} className="group flex cursor-pointer items-center gap-3">
                    <input
                      type="radio"
                      value={index}
                      className={RADIO_CLS}
                      name={budgetField.name}
                      checked={budgetField.value === index}
                      onChange={() => budgetField.onChange(index)}
                      onBlur={budgetField.onBlur}
                      ref={index === 0 ? budgetField.ref : undefined}
                    />
                    <span className="transition-colors group-hover:text-[--state-brand-active]">
                      {budget}
                    </span>
                  </label>
                ))}
              </div>
              {errors.budget?.message && (
                <p className="mt-2 text-sm text-red-400">{errors.budget.message}</p>
              )}
            </div>

            {/* Delivery Time */}
            <div>
              <Label className="mb-3 block">Delivery time</Label>
              <div className="space-y-3">
                {enums?.deliveryTimes.map((time, index) => (
                  <label key={time} className="group flex cursor-pointer items-center gap-3">
                    <input
                      type="radio"
                      value={index}
                      className={RADIO_CLS}
                      aria-label={time}
                      name={deliveryField.name}
                      checked={deliveryField.value === index}
                      onChange={() => deliveryField.onChange(index)}
                      onBlur={deliveryField.onBlur}
                      ref={index === 0 ? deliveryField.ref : undefined}
                    />
                    <span className="transition-colors group-hover:text-[--state-brand-active]">
                      {time}
                    </span>

                    {/* Date picker inline when "Specific date" is selected */}
                    {index === 3 && deliveryTimeValue === 3 && (
                      <span className="relative ml-2 w-44">
                        <input
                          type="date"
                          aria-label="Specific delivery date"
                          className={`${INPUT_CLS} w-full`}
                          {...register('deliveryDate')}
                        />
                        <i className="i-calendar-2-line absolute right-4 top-1/2 -translate-y-1/2 text-[--base-fill-5]" />
                      </span>
                    )}
                  </label>
                ))}
              </div>
              {errors.deliveryTime?.message && (
                <p className="mt-2 text-sm text-red-400">{errors.deliveryTime.message}</p>
              )}
              {errors.deliveryDate?.message && (
                <p className="mt-2 text-sm text-red-400">{errors.deliveryDate.message}</p>
              )}
            </div>
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between">
            <Button type="button" variant="secondary" onClick={onBack}>
              <i className="ri-arrow-left-line" />
              Back
            </Button>
            <Button type="button" onClick={onNext}>
              Next: Review &amp; Submit
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
